import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy, limit, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuthStore } from '../store';
import { Calendar, Bell, X, ArrowRight, MapPin, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from '@tanstack/react-router';

interface AppNotification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: string;
  data?: any;
  read: boolean;
  createdAt: any;
}

export function PickupNotifier() {
  const { user } = useAuthStore();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }

    // Fetch unread notifications
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(5)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AppNotification[];
      setNotifications(notifs);
    }, (error) => {
      console.error("Error fetching notifications:", error);
    });

    return () => unsubscribe();
  }, [user]);

  const markAsRead = async (id: string) => {
    try {
      await updateDoc(doc(db, 'notifications', id), {
        read: true
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-6 right-6 md:right-auto md:max-w-md z-[60] space-y-3">
      <AnimatePresence>
        {notifications.map((notif) => (
          <motion.div
            key={notif.id}
            initial={{ opacity: 0, x: -20, y: 20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`p-4 rounded-2xl shadow-xl border backdrop-blur-md transition-all ${
              notif.type === 'pickup_reminder' 
                ? 'bg-amber-50/90 dark:bg-amber-900/40 border-amber-200 dark:border-amber-900/50 text-amber-900 dark:text-amber-100'
                : 'bg-blue-50/90 dark:bg-blue-900/40 border-blue-200 dark:border-blue-900/50 text-blue-900 dark:text-blue-100'
            }`}
          >
            <div className="flex gap-4">
              <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                notif.type === 'pickup_reminder' ? 'bg-amber-200/50 dark:bg-amber-500/20' : 'bg-blue-200/50 dark:bg-blue-500/20'
              }`}>
                {notif.type === 'pickup_reminder' ? (
                  <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                ) : (
                  <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-bold flex items-center gap-1.5">
                    {notif.title}
                  </h4>
                  <button 
                    onClick={() => markAsRead(notif.id)}
                    className="p-1 hover:bg-black/5 dark:hover:bg-white/5 rounded-md transition-colors"
                  >
                    <X className="w-4 h-4 opacity-50" />
                  </button>
                </div>
                
                <p className="text-sm mt-1 opacity-90 leading-relaxed">
                  {notif.message}
                </p>

                {notif.data?.location && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs font-medium opacity-80">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="truncate">{notif.data.location}</span>
                  </div>
                )}
                
                <div className="mt-3 flex items-center gap-3">
                  <Link 
                    to={notif.data?.transactionId ? `/profile` : '/'}
                    onClick={() => markAsRead(notif.id)}
                    className={`text-xs font-bold flex items-center gap-1 hover:underline ${
                      notif.type === 'pickup_reminder' ? 'text-amber-700 dark:text-amber-300' : 'text-blue-700 dark:text-blue-300'
                    }`}
                  >
                    Details
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                  
                  {notif.data?.location && (
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(notif.data.location)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold flex items-center gap-1 hover:underline opacity-80"
                    >
                      Maps
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
